import PropTypes from "prop-types";

import ImmutablePropTypes from "react-immutable-proptypes";
import ImmutablePureComponent from "react-immutable-pure-component";

import { debounce } from "lodash";

import RegenerationIndicator from "flavours/glitch/components/regeneration_indicator";
import StatusContainer from "flavours/glitch/containers/status_container";
import { firstSeenId } from "flavours/glitch/utils/read_marker";

import { LoadGap } from "./load_gap";
import ScrollableList from "./scrollable_list";

export default class StatusList extends ImmutablePureComponent {

  static propTypes = {
    scrollKey: PropTypes.string.isRequired,
    statusIds: ImmutablePropTypes.list.isRequired,
    featuredStatusIds: ImmutablePropTypes.list,
    readMarker: PropTypes.string,
    onLoadMore: PropTypes.func,
    onLoadGapUpward: PropTypes.func,
    bindToDocument: PropTypes.bool,
    onScrollToTop: PropTypes.func,
    onScroll: PropTypes.func,
    trackScroll: PropTypes.bool,
    isLoading: PropTypes.bool,
    isPartial: PropTypes.bool,
    hasMore: PropTypes.bool,
    prepend: PropTypes.node,
    emptyMessage: PropTypes.node,
    alwaysPrepend: PropTypes.bool,
    withCounters: PropTypes.bool,
    timelineId: PropTypes.string.isRequired,
    lastId: PropTypes.string,
    regex: PropTypes.string,
  };

  static defaultProps = {
    trackScroll: true,
  };

  getFeaturedStatusCount = () => {
    return this.props.featuredStatusIds ? this.props.featuredStatusIds.size : 0;
  };

  getCurrentStatusIndex = (id, featured) => {
    if (featured) {
      return this.props.featuredStatusIds.indexOf(id);
    } else {
      return this.props.statusIds.indexOf(id) + this.getFeaturedStatusCount();
    }
  };

  handleMoveUp = (id, featured) => {
    const elementIndex = this.getCurrentStatusIndex(id, featured) - 1;
    this._selectChild(elementIndex, true);
  };

  handleMoveDown = (id, featured) => {
    const elementIndex = this.getCurrentStatusIndex(id, featured) + 1;
    this._selectChild(elementIndex, false);
  };

  // A gap is filled from its lower end (fillTimelineGapUpward), so the post below
  // it keeps its place on screen while the loaded posts appear above it. Timelines
  // that cannot do that load from the upper end as before.
  handleLoadGap = (maxId, belowId) => {
    const { onLoadGapUpward, onLoadMore } = this.props;

    if (belowId && onLoadGapUpward && onLoadGapUpward(belowId)) {
      this.gapAnchorId = belowId;
      return;
    }

    onLoadMore(maxId);
  };

  _findArticle (id) {
    return this.node?.node?.querySelector(`article[data-id="${CSS.escape(id)}"]`);
  }

  getSnapshotBeforeUpdate (prevProps) {
    if (this.gapAnchorId && prevProps.statusIds !== this.props.statusIds) {
      const article = this._findArticle(this.gapAnchorId);
      return article ? article.getBoundingClientRect().top : null;
    }
    return null;
  }

  componentDidUpdate (prevProps, prevState, anchorTop) {
    if (anchorTop !== null && anchorTop !== undefined) {
      const article = this._findArticle(this.gapAnchorId);
      const delta = article ? article.getBoundingClientRect().top - anchorTop : 0;

      if (delta !== 0) {
        if (this.props.bindToDocument) {
          window.scrollBy(0, delta);
        } else if (this.node?.node) {
          this.node.node.scrollTop += delta;
        }
      }
    }

    if (this.gapAnchorId && prevProps.isLoading && !this.props.isLoading) {
      this.gapAnchorId = null;
    }
  }

  handleLoadOlder = debounce(() => {
    const { statusIds, lastId, onLoadMore } = this.props;
    onLoadMore(lastId || (statusIds.size > 0 ? statusIds.last() : undefined));
  }, 300, { leading: true });

  _selectChild (index, align_top) {
    const container = this.node.node;
    const element = container.querySelector(`article:nth-of-type(${index + 1}) .focusable`);

    if (element) {
      if (align_top && container.scrollTop > element.offsetTop) {
        element.scrollIntoView(true);
      } else if (!align_top && container.scrollTop + container.clientHeight < element.offsetTop + element.offsetHeight) {
        element.scrollIntoView(false);
      }
      element.focus();
    }
  }

  setRef = c => {
    this.node = c;
  };

  render () {
    const { statusIds, featuredStatusIds, onLoadMore, onLoadGapUpward, timelineId, readMarker, ...other }  = this.props;
    const markedId = firstSeenId(statusIds, readMarker);
    const { isLoading, isPartial } = other;

    if (isPartial) {
      return <RegenerationIndicator />;
    }

    let scrollableContent = (isLoading || statusIds.size > 0) ? (
      statusIds.map((statusId, index) => statusId === null ? (
        <LoadGap
          key={"gap:" + statusIds.get(index + 1)}
          disabled={isLoading}
          maxId={index > 0 ? statusIds.get(index - 1) : null}
          belowId={statusIds.get(index + 1)}
          onClick={this.handleLoadGap}
        />
      ) : (
        <StatusContainer
          key={statusId}
          id={statusId}
          onMoveUp={this.handleMoveUp}
          onMoveDown={this.handleMoveDown}
          contextType={timelineId}
          scrollKey={this.props.scrollKey}
          withCounters={this.props.withCounters}
          readMarker={statusId === markedId}
        />
      ))
    ) : null;

    if (scrollableContent && featuredStatusIds) {
      scrollableContent = featuredStatusIds.map(statusId => (
        <StatusContainer
          key={`f-${statusId}`}
          id={statusId}
          featured
          onMoveUp={this.handleMoveUp}
          onMoveDown={this.handleMoveDown}
          contextType={timelineId}
          scrollKey={this.props.scrollKey}
          withCounters={this.props.withCounters}
        />
      )).concat(scrollableContent);
    }

    return (
      <ScrollableList {...other} showLoading={isLoading && statusIds.size === 0} onLoadMore={onLoadMore && this.handleLoadOlder} ref={this.setRef}>
        {scrollableContent}
      </ScrollableList>
    );
  }

}
